import React, { useEffect, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { API } from '@/utils/API';
import { Button } from '@/components/ui/button';
import type { QuestionData } from '../types';

interface VerifyMatch {
  id: string;
  value: string;
}

interface LookupResult {
  name: string;
  credential: string;
  detail: string;
  phone: string;
  external_source: string;
  external_id: string;
  components: Record<string, string>;
}

interface AssistAddonsProps {
  question: QuestionData;
  submissionId: string;
  verifyEnabled: boolean;
  lookupEnabled: boolean;
  children: React.ReactNode;
}

/** jsonb answers can be anything; only text kinds have something to verify. */
const asText = (value: unknown): string | null =>
  typeof value === 'string' ? value : null;

/**
 * Verify Answer (Feature 2) + Public Collections (Feature 3) attach to any
 * field by wrapping it: on blur the wrapper runs the configured checks and
 * renders a "did you mean?" prompt or a fetched public-record card under the
 * field. Order per spec: lookup first; verify only when lookup found nothing.
 * Every failure path renders nothing — assist, never interference.
 *
 * Blur can fire faster than the endpoints answer (typing, tabbing back,
 * fixing the value, tabbing out again), so every request pair carries a
 * sequence number and an AbortController: only the newest one may write state.
 * Without that a slow first response lands after a corrected second one and
 * prompts "Did you mean X?" for a field that already reads exactly X.
 */
export const AssistAddons: React.FC<AssistAddonsProps> = ({
  question,
  submissionId,
  verifyEnabled,
  lookupEnabled,
  children,
}) => {
  const { setValue, getValues } = useFormContext();
  const [verifyMatch, setVerifyMatch] = useState<VerifyMatch | null>(null);
  const [lookupResult, setLookupResult] = useState<LookupResult | null>(null);

  // Refs, not state: both are read *after* an await, where a state value
  // captured in the closure would be the one from the render that started the
  // request rather than the current one.
  const dismissedForRef = useRef<string | null>(null);
  const requestSeqRef = useRef(0);
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => () => abortRef.current?.abort(), []);

  const runLookup = async (
    text: string,
    signal: AbortSignal,
    isCurrent: () => boolean
  ): Promise<boolean> => {
    try {
      const res = await API.authenticated.fetch(
        `/submissions/${submissionId}/public_lookup`,
        {
          method: 'POST',
          body: JSON.stringify({ lookup: { slug: question.slug, text } }),
          signal,
        }
      );
      if (!res.ok) return false;
      const body = (await res.json()) as { result: LookupResult | null };
      if (!isCurrent() || dismissedForRef.current === text) return false;
      if (body.result) {
        setLookupResult(body.result);
        return true;
      }
      return false;
    } catch {
      return false;
    }
  };

  const runVerify = async (
    text: string,
    signal: AbortSignal,
    isCurrent: () => boolean
  ) => {
    try {
      const res = await API.authenticated.fetch(
        `/submissions/${submissionId}/answer_verify`,
        {
          method: 'POST',
          body: JSON.stringify({ verification: { slug: question.slug, text } }),
          signal,
        }
      );
      if (!res.ok) return;
      const body = (await res.json()) as {
        match: VerifyMatch | null;
        exact?: boolean;
      };
      if (!isCurrent() || dismissedForRef.current === text) return;
      if (body.match && !body.exact && body.match.value !== text) {
        setVerifyMatch(body.match);
      }
    } catch {
      // silence — plain field behavior
    }
  };

  const handleBlur = async () => {
    // Neither addon active → do nothing. FormField only mounts this wrapper
    // when at least one addon is actually active (see FormField.tsx), so this
    // is a guard for direct/standalone use rather than the common path.
    if (!verifyEnabled && !lookupEnabled) return;

    // jsonb answers: array/object-valued kinds (select_multi, upload) hand
    // back a non-string here. Verify/lookup only apply to text kinds.
    const raw = asText(getValues(question.slug));
    if (raw === null) return;
    const text = raw.trim();

    // Supersede anything still in flight — only the newest blur may render.
    abortRef.current?.abort();
    const seq = requestSeqRef.current + 1;
    requestSeqRef.current = seq;
    const controller = new AbortController();
    abortRef.current = controller;
    const isCurrent = () =>
      seq === requestSeqRef.current && !controller.signal.aborted;

    setLookupResult(null);
    setVerifyMatch(null);
    if (!text || text === dismissedForRef.current) return;

    const found = lookupEnabled
      ? await runLookup(text, controller.signal, isCurrent)
      : false;
    if (!isCurrent()) return;
    if (!found && verifyEnabled) {
      await runVerify(text, controller.signal, isCurrent);
    }
  };

  const acceptValue = (value: string) => {
    setValue(question.slug, value, { shouldDirty: true, shouldValidate: true });
    setVerifyMatch(null);
    setLookupResult(null);
  };

  const dismiss = () => {
    dismissedForRef.current = (asText(getValues(question.slug)) ?? '').trim();
    setVerifyMatch(null);
    setLookupResult(null);
  };

  return (
    <div onBlurCapture={() => void handleBlur()}>
      {children}
      {verifyMatch && (
        <div
          className="mt-2 rounded-md border border-amber-400 bg-amber-50 px-3 py-2 text-sm text-amber-900"
          role="status"
          aria-live="polite"
          data-testid={`verify-prompt-${question.slug}`}
        >
          Did you mean <strong>{verifyMatch.value}</strong>?
          <div className="mt-2 flex gap-2">
            <Button type="button" size="sm" onClick={() => acceptValue(verifyMatch.value)}>
              Use suggestion
            </Button>
            <Button type="button" size="sm" variant="outline" onClick={dismiss}>
              Keep what I typed
            </Button>
          </div>
        </div>
      )}
      {lookupResult && (
        <div
          className="mt-2 rounded-md border border-blue-400 bg-blue-50 px-3 py-2 text-sm text-blue-900"
          role="status"
          aria-live="polite"
          data-testid={`lookup-card-${question.slug}`}
        >
          <p className="font-semibold">
            {lookupResult.name}
            {lookupResult.credential ? `, ${lookupResult.credential}` : ''}
          </p>
          {lookupResult.detail && <p className="text-xs">{lookupResult.detail}</p>}
          <p className="mt-1 text-[10px] uppercase tracking-wide text-blue-700 font-mono">
            {lookupResult.external_source} · {lookupResult.external_id} · public record
          </p>
          <div className="mt-2 flex gap-2">
            <Button
              type="button"
              size="sm"
              onClick={() =>
                acceptValue(
                  lookupResult.credential
                    ? `${lookupResult.name}, ${lookupResult.credential}`
                    : lookupResult.name
                )
              }
            >
              Use this
            </Button>
            <Button type="button" size="sm" variant="outline" onClick={dismiss}>
              Keep what I typed
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};
